/**
 * @file        src/pages/account/Transactions.jsx
 * @description Full ledger view (Account Management → Transaction Log).
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-30 Claude — The log now unions the WALLET ledger with the gateway
 *                          one (money settled from a customer's Neon Wallet used
 *                          to appear nowhere), so rows carry a "Paid With"
 *                          instrument and the KPIs split the debit total.
 *   - 2026-08-03 Claude — Debit/Credit are now stated from NEON'S books: Credit
 *                          is money we received from the customer, Debit is money
 *                          we paid out (supplier fare + refunds). Column order,
 *                          field names, filters, paging and the CSV shape are
 *                          unchanged — the amounts simply land in the column that
 *                          matches their business meaning. Values come from the
 *                          API exactly as before; the mapping lives in
 *                          transactions.repo.js.
 *   - 2026-08-04 Claude — BOOKING-GROUPED: one row per booking (the API groups
 *                          now), identified by its booking REFERENCE instead of a
 *                          raw per-source ledger id. A part-wallet booking is one
 *                          row whose wallet/gateway split expands in place, and
 *                          each flight/hotel row opens the existing
 *                          BookingDetailsDrawer read-only for the full picture.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { Fragment, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import FlightBookingDetailsDrawer from '../flight/BookingDetailsDrawer';
import HotelBookingDetailsDrawer from '../hotel/BookingDetailsDrawer';
import { get } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { inr, formatDateTime } from '../../lib/format';
import { toast } from '../../lib/toast';

const FLOW_LABEL = { flight: 'Flight', hotel: 'Hotel', insurance: 'Insurance' };

const num = (v) => Number(v) || 0;

/** The individual money movements behind a booking row, for the expanded view. */
function legsOf(r) {
  const legs = [];
  // gateway_amount already includes SSR/Reissue extras — show them apart.
  const gatewayBooking = num(r.gateway_amount) - num(r.extras_amount);
  if (gatewayBooking > 0) legs.push({ label: 'Received · Gateway', amount: gatewayBooking, side: 'cre' });
  if (num(r.wallet_amount) > 0) legs.push({ label: 'Received · Wallet', amount: num(r.wallet_amount), side: 'cre' });
  if (num(r.extras_amount) > 0) legs.push({ label: 'Received · Add-ons (SSR / Reissue)', amount: num(r.extras_amount), side: 'cre' });
  if (num(r.supplier_fare) > 0) legs.push({ label: 'Paid · Supplier fare', amount: num(r.supplier_fare), side: 'deb' });
  if (num(r.refund_gateway) > 0) legs.push({ label: 'Refunded · to card', amount: num(r.refund_gateway), side: 'deb' });
  if (num(r.refund_wallet) > 0) legs.push({ label: 'Refunded · to wallet', amount: num(r.refund_wallet), side: 'deb' });
  return legs;
}

function paidWith(r) {
  const wallet = num(r.wallet_amount) > 0;
  const gateway = num(r.gateway_amount) > 0;
  if (wallet && gateway) return 'Wallet + Gateway';
  if (wallet) return 'Wallet';
  if (gateway) return 'Gateway';
  return null;
}

// CSV cell escape; the export carries the same columns the table shows.
const csvEsc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const refOf = (r) => r.booking_reference || `ID-${r.booking_id}`;

export default function Transactions() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [flowType, setFlowType] = useState('');
  const [exporting, setExporting] = useState(false);
  const [openKey, setOpenKey] = useState(null);   // row_key of the expanded booking
  const [drawer, setDrawer] = useState(null);     // { flow: 'flight'|'hotel', id }

  // Export ALL rows for the current filter (not just the visible page). The list API
  // caps pageSize at 200, so page through it; MAX_ROWS is a safety cap for a huge ledger.
  async function onExport() {
    setExporting(true);
    try {
      const EXPORT_PAGE = 200;
      const MAX_ROWS = 5000;
      let all = [];
      let pageN = 1;
      let totalRows = 0;
      do {
        const params = { page: pageN, pageSize: EXPORT_PAGE, ...(flowType ? { flowType } : {}) };
        const res = await get('/transactions', { params });
        const batch = res?.data || [];
        totalRows = res?.meta?.total || 0;
        all = all.concat(batch);
        pageN += 1;
        if (batch.length < EXPORT_PAGE) break;
      } while (all.length < totalRows && all.length < MAX_ROWS);

      if (!all.length) { toast.err('Nothing to export', 'No transactions match this filter.'); return; }

      const lines = [
        ['Booking Ref', 'Product', 'Paid With', 'Wallet', 'Gateway', 'Debit', 'Credit', 'Status', 'Date'].join(','),
        ...all.map((r) => [
          csvEsc(refOf(r)),
          csvEsc(FLOW_LABEL[r.flow_type] ?? 'Flight'),
          csvEsc(paidWith(r) ?? '—'),
          num(r.wallet_amount),
          num(r.gateway_amount),
          num(r.debit_amount),
          num(r.credit_amount),
          csvEsc(r.status),
          csvEsc(formatDateTime(r.created_at)),
        ].join(',')),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);

      if (all.length >= MAX_ROWS && all.length < totalRows) {
        toast.err('Export truncated', `Only the first ${MAX_ROWS} transactions were exported.`);
      } else {
        toast.ok('Exported', `${all.length} booking${all.length > 1 ? 's' : ''} downloaded.`);
      }
    } catch (e) {
      toast.err('Export failed', e.response?.data?.error?.message || 'Could not export transactions.');
    } finally {
      setExporting(false);
    }
  }

  const listParams = { page, pageSize, ...(flowType ? { flowType } : {}) };
  const { data: list, isLoading } = useQuery({
    queryKey: qk.transactions.list(listParams),
    queryFn: () => get('/transactions', { params: listParams }),
    placeholderData: keepPreviousData,
  });

  const totalsParams = flowType ? { flowType } : {};
  const { data: totals } = useQuery({
    // Same filter as the table, so the KPI cards match the rows shown below.
    queryKey: qk.transactions.totals(totalsParams),
    queryFn: () => get('/transactions/totals', { params: totalsParams }).then((r) => r.data),
  });

  const rows = list?.data || [];
  const total = list?.meta?.total || 0;

  return (
    <>
      <PageHead
        title="Transaction Log"
        // Spelling the convention out on the page itself: "debit" and "credit"
        // read backwards to anyone thinking from the customer's side, and this
        // ledger is kept from Neon's.
        subtitle="One row per booking · Credit = received from customer · Debit = paid to supplier + refunds"
        actions={
          <button className="btn primary" onClick={onExport} disabled={exporting}>
            <Icon name="download" /> {exporting ? 'Exporting…' : 'Export'}
          </button>
        }
      />

      <div className="mini-kpi">
        <article className="mk glass neo"><small>Total Bookings</small><b>{totals?.total_count ?? '—'}</b></article>
        <article className="mk glass neo"><small>Total Credit</small><b className="cre">{totals ? inr(totals.total_credit) : '—'}</b></article>
        <article className="mk glass neo"><small>Total Debit</small><b className="deb">{totals ? inr(totals.total_debit) : '—'}</b></article>
        {/* Money RECEIVED, split by the instrument it came in through; the two
            add up to Total Credit. */}
        <article className="mk glass neo"><small>From Wallet</small><b>{totals ? inr(totals.total_wallet) : '—'}</b></article>
        <article className="mk glass neo"><small>From Gateway</small><b>{totals ? inr(totals.total_gateway) : '—'}</b></article>
      </div>

      <article className="panel glass">
        <div className="search-row">
          <div className="field">
            <label>Flow Type</label>
            <Select
              value={flowType}
              onChange={(v) => { setFlowType(v); setPage(1); setOpenKey(null); }}
              options={[{ value: '', label: 'All' }, { value: 'flight', label: 'Flight' }, { value: 'hotel', label: 'Hotel' }, { value: 'insurance', label: 'Insurance' }]}
            />
          </div>
        </div>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>Booking Ref</th><th>Detail</th><th>Paid With</th>
                  <th title="Money paid out by Neon — the supplier's fare for this booking, plus any refund returned to the customer">Debit</th>
                  <th title="Money received by Neon from the customer, wallet and gateway combined">Credit</th>
                  <th>Status</th><th>Date</th><th aria-label="Expand" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No transactions for these filters.</td></tr>
                ) : rows.map((r) => {
                  const open = openKey === r.row_key;
                  const legs = legsOf(r);
                  const instrument = paidWith(r);
                  const hasDrawer = r.flow_type === 'flight' || r.flow_type === 'hotel';
                  return (
                    <Fragment key={r.row_key}>
                      <tr
                        onClick={() => setOpenKey(open ? null : r.row_key)}
                        style={{ cursor: 'pointer' }}
                        aria-expanded={open}
                      >
                        <td className="mono">{refOf(r)}</td>
                        <td>{FLOW_LABEL[r.flow_type] ?? 'Flight'} Booking (ID-{r.booking_id})</td>
                        <td>
                          {instrument
                            ? <span className={`chip ${num(r.wallet_amount) > 0 ? 'ok' : 'warn'}`}>{instrument}</span>
                            : <span className="muted">—</span>}
                        </td>
                        <td className="deb">{num(r.debit_amount) > 0 ? inr(r.debit_amount) : '—'}</td>
                        <td className="cre">{num(r.credit_amount) > 0 ? inr(r.credit_amount) : '—'}</td>
                        <td className="muted">{r.status}</td>
                        <td className="muted">{formatDateTime(r.created_at)}</td>
                        <td className="muted" style={{ width: 28, textAlign: 'center' }}>{open ? '▾' : '▸'}</td>
                      </tr>
                      {open ? (
                        <tr>
                          <td colSpan={8} style={{ background: 'rgba(148,163,184,.06)' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center', padding: '6px 4px' }}>
                              {legs.length === 0 ? (
                                <span className="muted">No money has moved on this booking yet.</span>
                              ) : legs.map((l) => (
                                <span key={l.label}>
                                  <span className="muted">{l.label}: </span>
                                  <b className={l.side}>{inr(l.amount)}</b>
                                </span>
                              ))}
                              {hasDrawer ? (
                                <button
                                  className="btn ghost sm"
                                  style={{ marginLeft: 'auto' }}
                                  onClick={(e) => { e.stopPropagation(); setDrawer({ flow: r.flow_type, id: r.booking_id }); }}
                                >
                                  <Icon name="page" /> View booking
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={(p) => { setPage(p); setOpenKey(null); }} />
      </article>

      {/* Read-only booking detail — the same drawer the Booking List uses, so
          there is exactly one place that knows how to show a booking. */}
      {drawer?.flow === 'flight' ? (
        <FlightBookingDetailsDrawer bookingId={drawer.id} onClose={() => setDrawer(null)} />
      ) : null}
      {drawer?.flow === 'hotel' ? (
        <HotelBookingDetailsDrawer bookingId={drawer.id} onClose={() => setDrawer(null)} />
      ) : null}
    </>
  );
}
